import { useCallback, useMemo } from 'react';

import { NotificationActions } from 'domains/notification/store/NotificationActions';
import { ReferralProgram } from 'modules/strapi/types';
import { getReferralCodeFromUrl } from 'modules/referralProgram/utils/getReferralCodeFromUrl';
import { referralProgramTranslation } from 'modules/referralProgram/translation';
import { removeReferralCodeFromUrl } from 'modules/referralProgram/utils/removeReferralCodeFromUrl';
import { useAppDispatch } from 'store/useAppDispatch';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useDialog } from 'modules/common/hooks/useDialog';
import { useSSOAuth } from 'modules/auth/hooks/useSSOAuth';
import { useSavedReferralCode } from 'modules/referralProgram/hooks/useSavedReferralCode';
import { useSignOut } from 'modules/auth/hooks/useSignOut';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';
import { waitForLocalStorageSync } from 'modules/common/utils/waitForLocalStorageSync';

import { IIneligibleAccountDialogProps } from '../IneligibleAccountDialog';

const { showNotification } = NotificationActions;

export interface IUseIneligibleAccountDialogProps {
  cover: string;
  referralProgram: ReferralProgram | undefined;
  setIsLoggingOut: (isLoggingOut: boolean) => void;
}

export const useIneligibleAccountDialog = ({
  cover,
  referralProgram,
  setIsLoggingOut,
}: IUseIneligibleAccountDialogProps) => {
  const {
    isOpened,
    onClose: handleClose,
    onOpen: handleIneligibleAccountDialogOpen,
  } = useDialog();

  const { referralCode: referralCodeFromAuthLink } = useAuth();

  const { handleSignOut } = useSignOut();

  const { referralCode: referralCodeFromURL } = getReferralCodeFromUrl();

  const referralCode = referralCodeFromAuthLink || referralCodeFromURL;

  // sso auth hook is used to build auth URL with referral code
  const { handleSignIn: signIn } = useSSOAuth({ referralCode });

  const handleSignIn = useCallback(async () => {
    setIsLoggingOut(true);

    handleSignOut();

    await waitForLocalStorageSync();

    signIn();
  }, [handleSignOut, setIsLoggingOut, signIn]);

  const { handleRemoveSavedReferralCode } = useSavedReferralCode();

  const {
    keys: { branded, unbranded },
    t,
  } = useTranslation(referralProgramTranslation);

  const keys = referralProgram ? branded : unbranded;

  const dispatch = useAppDispatch();

  const referralProgramName = referralProgram?.name;

  const onClose = useCallback(() => {
    removeReferralCodeFromUrl();
    handleRemoveSavedReferralCode();

    handleClose();

    dispatch(
      showNotification({
        message: t(keys.activationRejectedErrorMessage),
        severity: 'error',
        title: t(keys.activationRejectedErrorTitle, { referralProgramName }),
      }),
    );
  }, [
    dispatch,
    handleClose,
    handleRemoveSavedReferralCode,
    keys,
    referralProgramName,
    t,
  ]);

  const ineligibleAccountDialogProps = useMemo(
    (): IIneligibleAccountDialogProps => ({
      cover,
      onClose,
      onSignInButtonClick: handleSignIn,
      open: isOpened,
      referralProgramName,
    }),
    [cover, handleSignIn, isOpened, onClose, referralProgramName],
  );

  return { handleIneligibleAccountDialogOpen, ineligibleAccountDialogProps };
};
